import { useEffect, useState } from "react";
import { HelpCircle, Lightbulb, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";

function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

function Body({ purpose, bullets = [], example }) {
  return (
    <div className="space-y-2 text-sm" data-testid="field-help-body">
      <p className="text-muted-foreground">{purpose}</p>
      {bullets.length > 0 && <ul className="list-disc pl-4 space-y-1">{bullets.map((b, i) => <li key={`${b}-${i}`}>{b}</li>)}</ul>}
      {example && <p className="text-xs"><span className="text-amber-500 font-medium">Example:</span> <span className="text-muted-foreground">{example}</span></p>}
    </div>
  );
}

export function FieldHelp({ title, purpose, bullets = [], example, testid }) {
  const mobile = useIsMobile();
  const trigger = (
    <button type="button" aria-label={`How to fill: ${title}`}
      data-testid={testid || `help-${(title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="inline-flex items-center justify-center h-11 w-11 -my-3 -mx-2.5 md:h-6 md:w-6 md:m-0 text-muted-foreground hover:text-amber-500 transition-colors align-middle">
      <HelpCircle className="h-4 w-4 md:h-3.5 md:w-3.5" />
    </button>
  );
  if (mobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh] overflow-y-auto">
          <SheetHeader><SheetTitle>{title}</SheetTitle></SheetHeader>
          <div className="mt-3 pb-8"><Body purpose={purpose} bullets={bullets} example={example} /></div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="font-medium text-sm mb-2">{title}</div>
        <Body purpose={purpose} bullets={bullets} example={example} />
      </PopoverContent>
    </Popover>
  );
}

export function LabelHelp({ label, help, testid, className }) {
  return (
    <div className={`flex items-center gap-1.5 ${className || ""}`}>
      <Label>{label}</Label>
      {help && <FieldHelp title={label} testid={testid} {...help} />}
    </div>
  );
}

export function PageTip({ id, children }) {
  const key = `dealsops_tip_${id}`;
  const [show, setShow] = useState(false);
  useEffect(() => { try { setShow(!localStorage.getItem(key)); } catch { /* noop */ } }, [key]);
  if (!show) return null;
  const dismiss = () => { try { localStorage.setItem(key, "1"); } catch { /* noop */ } setShow(false); };
  return (
    <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3" data-testid={`page-tip-${id}`}>
      <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
      <div className="text-sm text-muted-foreground flex-1">{children}</div>
      <button onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`dismiss-tip-${id}`} aria-label="Dismiss tip"><X className="h-4 w-4" /></button>
    </div>
  );
}
